import {
  firmLibraryItemSchema,
  firmLibraryListSchema,
  firmLibraryRenameRequestSchema,
  type FirmLibraryItem,
  type FirmLibraryList,
  type FirmLibraryRenameRequest,
} from "@contract/library/library";
import { z } from "zod";
import { getSupabase } from "../auth/supabase";
import { env } from "../env";
import { apiRequest } from "./client";

/** GET /v1/firm/library: the firm library, your own saved components and the firm's usage
 * against its limit (firm-model spec §8, §10.2). Every active member may read. */
export function fetchFirmLibrary(): Promise<FirmLibraryList> {
  return apiRequest("/v1/firm/library", {}, (json) => firmLibraryListSchema.parse(json));
}

/** PATCH /v1/firm/library/:id (`edit_library`). */
export function renameFirmLibraryItem(id: string, name: string): Promise<FirmLibraryItem> {
  const body: FirmLibraryRenameRequest = firmLibraryRenameRequestSchema.parse({ name });
  return apiRequest(
    `/v1/firm/library/${encodeURIComponent(id)}`,
    { method: "PATCH", body },
    (json) => z.object({ item: firmLibraryItemSchema }).parse(json).item,
  );
}

/** DELETE /v1/firm/library/:id (`edit_library`). */
export async function removeFirmLibraryItem(id: string): Promise<void> {
  await apiRequest(`/v1/firm/library/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** A thumbnail that hangs gives up after this and the row shows no picture. */
const THUMB_TIMEOUT_MS = 10_000;

/** A component's thumbnail as an object URL (the file route needs the session token, so a
 * plain <img src> can't load it). Null when it can't be loaded: the row shows no picture.
 * The caller owns the URL and revokes it (`URL.revokeObjectURL`) when it is done. */
export async function fetchThumbUrl(id: string, fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    const { data } = await getSupabase().auth.getSession();
    const token = data.session?.access_token;
    const res = await fetchImpl(`${env.apiUrl}/v1/library/file?id=${encodeURIComponent(id)}&kind=thumb`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
      signal: AbortSignal.timeout(THUMB_TIMEOUT_MS),
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  } catch {
    return null;
  }
}
